import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import NsColumn from '@britishgasenergy/react-nucleus/dist/nuc-components/NsColumn';
import NsContent from 'components/common/ns-content/NsContent';

import { NsCard } from 'components/common/ns-card/NsCard';
import { NsPanel } from 'components/common/ns-panel/NsPanel';
import { NsTitle } from 'components/common/ns-title/NsTitle';
import { NsAnchor } from 'components/common/ns-anchor/NsAnchor';
import { NsCaveat } from 'components/common/ns-caveat/NsCaveat';
import { AboutSolar } from './components/AboutSolar';
import { SolarFAQ } from './components/SolarFAQ';
import { RegistrationForm } from 'components/common/registration-form/RegistrationForm';
import {
  setFormField,
  selectDetails,
  selectErrors,
  selectIsLoading
} from 'state/solar-registration/solar-registration-slice';
import { sendSolarSignupData } from 'state/solar-registration/solar-registration-thunks';
import { useNavigation } from 'hooks/useNavigation';
import { NsCaveatLink } from 'components/common/ns-caveat-link/NsCaveatLink';

export const testId = 'solarRegistrationTestId';

export const SolarRegistration = () => {
  const dispatch = useDispatch();
  const apiErrors = useSelector(selectErrors, shallowEqual);
  const details = useSelector(selectDetails, shallowEqual);
  const isLoading = useSelector(selectIsLoading, shallowEqual);
  const { next, eventTypes } = useNavigation();

  const handleGoNext = async (e) => {
    e.preventDefault();

    try {
      await dispatch(sendSolarSignupData(details));
      next({ eventType: eventTypes.NEXT });
    } catch (e) {
      e && console.error(e);
    }
  };

  const handleChange = (event) => {
    const {
      target: { name, processedValue: value }
    } = event;

    dispatch(setFormField({ value, name }));
  };

  return (
    <>
      <NsPanel decoration="grey-light" testId={testId}>
        <NsContent>
          <NsTitle title="Become a Hive Solar Saver" />
          <p className="p-normal">
            Exclusively for Hive solar and battery customers, Hive Solar Saver
            offers up to <b>25% off electricity rates for 12 months. </b>You can
            also earn by selling energy to the Grid with Export and Earn Plus,
            our smart export guarantee tariff. Simply sign up with your details
            and we&apos;ll take care of the rest.
          </p>
        </NsContent>
        <NsColumn hockeypitch="2">
          <NsCard
            type="section"
            decoration="savings"
            heading="Get a discount on your electricity"
          >
            <p>
              Save up to <b>94% on your electricity bills</b> with solar,
              battery, & our exclusive energy offer.
              <NsCaveatLink>
                Based on a medium demand (2,700 kWh) property in London, with
                south-facing 10 panel 4kW arrays and a battery providing 35% of
                the home’s electricity needs. Excess energy is sold back to the
                grid at 15.1p per kWh using the British Gas ‘Export & Earn Plus’
                SEG tariff. Saving includes ‘Hive Solar Saver’ discount of 25%
                on electricity unit rate in first year and savings will vary
                after that. Typical bill calculated using national average
                April-June 2025 price cap rates. Actual savings will depend on
                location, installation, aspect and electricity use.
              </NsCaveatLink>
            </p>
          </NsCard>
          <NsCard
            type="section"
            decoration="payments"
            heading="Save every month"
          >
            <p>
              After you sign up you&apos;ll receive up to 25% off the
              electricity you use for 12 months. We&apos;ll credit your bills
              with the difference.
              <NsCaveatLink>
                If you’re on a time-of-use tariff (e.g. Electric Driver or an
                Economy 7 tariff) the discount applies to peak rate consumption
                only.
              </NsCaveatLink>
            </p>
          </NsCard>
          <NsCard
            type="section"
            decoration="rosette-tick-green"
            heading="Free and flexible"
          >
            <p>
              It&apos;s free to sign up for our Hive Solar Saver add-on and you
              can switch from your current tariff to any of our other fixed
              energy tariffs at any time with no exit fee.
            </p>
          </NsCard>
          <NsCard
            type="section"
            decoration="homeSolarPanelGreen"
            heading="Earn even more"
          >
            <p>
              Hive Solar Saver customers can also join our Export and Earn Plus
              smart export guarantee tariff. That means you can earn by
              exporting excess power produced by your solar panels to the
              National Grid.
            </p>
          </NsCard>
        </NsColumn>
      </NsPanel>
      <AboutSolar />

      <RegistrationForm
        details={details}
        handleChange={handleChange}
        handleGoNext={handleGoNext}
        errors={apiErrors}
        isLoading={isLoading}
        termsPdf="https://www.britishgas.co.uk/aem6/content/dam/britishgas/documents/Hive-Solar-Saver-Terms-and-Conditions.pdf"
        termsPdfText="Hive Solar Saver Terms &amp; Conditions."
        termsCheckboxLabel={`
          I have read and understood the Terms and Conditions for the 
          Hive Solar Saver add-on.`}
        formOfferName="solar offer"
      />

      <SolarFAQ />
      <NsCaveat heading="Legal information">
        <p>
          Here&apos;s full{' '}
          <NsAnchor href="https://www.britishgas.co.uk/aem6/content/dam/britishgas/documents/Hive-Solar-Saver-Terms-and-Conditions.pdf">
            terms and conditions
          </NsAnchor>{' '}
          for the Hive Solar Saver add-on.
        </p>
      </NsCaveat>
    </>
  );
};

export default SolarRegistration;
